import { Component } from 'react';

import isEqual from 'lodash/isEqual';
import { range } from 'ramda';

import { BpkButtonV2 } from '@skyscanner/backpack-web/bpk-component-button';
import BpkLabel from '@skyscanner/backpack-web/bpk-component-label';
import BpkNudger from '@skyscanner/backpack-web/bpk-component-nudger';
import BpkSelect from '@skyscanner/backpack-web/bpk-component-select';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import type { TranslationKey } from '@shared/constants/translations';
import type { I18nService } from '@shared/types/i18n';
import type { ModalClose } from '@shared/ui/types';

import STYLES from './GuestsRoomsChildren.module.scss';

import { withI18n } from '@shared/ui/contexts/i18n';
import {
  MAX_ADULTS,
  MIN_ADULTS,
  MAX_ROOMS,
  MIN_ROOMS,
  MAX_CHILDREN,
  MIN_CHILDREN,
  MAX_CHILD_AGE,
  MIN_CHILD_AGE,
} from '@shared/utils-universal/guest-stay';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  adults: number;
  rooms: number;
  childrenAges: number[];
  onGuestsRoomsChildrenChanged: (
    rooms: number,
    adults: number,
    childrenAges: number[],
  ) => void;
  hasFooter?: boolean;
  onApply?: (shouldUpdate: boolean) => void;
  onClose?: ModalClose;
  isApplyButtonDisabled?: boolean;
};

type State = {
  rooms: number;
  adults: number;
  childrenAges: number[];
};

class GuestsRoomsChildren extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      rooms: props.rooms,
      adults: props.adults,
      childrenAges: props.childrenAges,
    };
  }

  // @ts-expect-error we need to get preStates but no prevProps
  componentDidUpdate(prevProps: Props, preStates: State) {
    const { adults, childrenAges, rooms } = this.state;
    if (!isEqual(preStates, this.state)) {
      this.props.onGuestsRoomsChildrenChanged(rooms, adults, childrenAges);
    }
  }

  roomsHandleChange = (rooms: number) => {
    const { adults } = this.state;
    if (rooms > adults) {
      this.setState({ adults: rooms });
    }
    this.setState({ rooms });
  };

  adultsHandleChange = (adults: number) => {
    const { rooms } = this.state;
    if (adults < rooms) {
      this.setState({ rooms: adults });
    }
    this.setState({ adults });
  };

  childrenHandleChange = (childrenAges: number) => {
    const [...newChildren] = this.state.childrenAges;
    if (childrenAges > this.state.childrenAges.length) {
      newChildren.push(0);
    } else if (childrenAges < this.state.childrenAges.length) {
      newChildren.pop();
    }

    this.setState({
      childrenAges: newChildren,
    });
  };

  childrenAgeHandleChange = (value: string, index: number) => {
    const { childrenAges } = this.state;
    const newChildren = childrenAges.map((v, i) => {
      if (i === index) {
        return Number.parseInt(value, 10);
      }
      return v;
    });

    this.setState({
      childrenAges: newChildren,
    });
  };

  render() {
    const { adults, childrenAges = [], rooms } = this.state;
    const { translate, translatePlural } = this.props.i18n;
    const childrenAgeLen = childrenAges.length;
    const { hasFooter, isApplyButtonDisabled, onApply, onClose } = this.props;

    const rangedOptions = range(MIN_CHILD_AGE, MAX_CHILD_AGE);

    return (
      <div className={STYLES.GuestsRoomsChildren}>
        <div className={STYLES.GuestsRoomsChildren__listItem}>
          <div>
            <BpkLabel htmlFor="adults">
              <BpkText tagName="h5" textStyle={TEXT_STYLES.heading5}>
                {translate('HotelStay_label_Adults')}
              </BpkText>
            </BpkLabel>
            <BpkText
              tagName="p"
              textStyle={TEXT_STYLES.bodyDefault}
              color={TEXT_COLORS.textSecondary}
            >
              {translate('guest_room_panel_adult_desc')}
            </BpkText>
          </div>
          <BpkNudger
            id="adults"
            min={MIN_ADULTS}
            max={MAX_ADULTS}
            value={adults}
            // @ts-expect-error TS2322: Property 'tabIndex' is not assignable.
            tabIndex={0}
            onValueChange={this.adultsHandleChange}
            decreaseButtonLabel="Decrease"
            increaseButtonLabel="Increase"
          />
        </div>

        <div className={STYLES.GuestsRoomsChildren__listItem}>
          <div>
            <BpkLabel htmlFor="children">
              <BpkText tagName="h5" textStyle={TEXT_STYLES.heading5}>
                {translate('HotelStay_label_Children')}
              </BpkText>
            </BpkLabel>
            <BpkText
              tagName="p"
              textStyle={TEXT_STYLES.bodyDefault}
              color={TEXT_COLORS.textSecondary}
            >
              {translate('guest_room_panel_child_desc')}
            </BpkText>
          </div>
          <BpkNudger
            id="children"
            min={MIN_CHILDREN}
            max={MAX_CHILDREN}
            value={childrenAgeLen}
            // @ts-expect-error TS2322: Property 'tabIndex' is not assignable.
            tabIndex={0}
            onValueChange={this.childrenHandleChange}
            decreaseButtonLabel="Decrease"
            increaseButtonLabel="Increase"
          />
        </div>

        {childrenAgeLen > 0 && (
          <>
            <BpkText
              tagName="p"
              className={cls('GuestsRoomsChildren__childrenDescription')}
              color={TEXT_COLORS.textSecondary}
            >
              {translate('guest_room_panel_child_add_age_reminder')}
            </BpkText>
            <section className={STYLES.GuestsRoomsChildren__popover}>
              {childrenAges.map((item, index) => (
                <>
                  <BpkLabel
                    htmlFor={`childrenAges-${index}`}
                    className={cls(
                      'GuestsRoomsChildren__label',
                      'GuestsRoomsChildren__labelNew',
                    )}
                  >
                    <BpkText textStyle={TEXT_STYLES.label2}>
                      {translate(
                        `HotelStay_label_Child_${index + 1}` as TranslationKey,
                      )}
                    </BpkText>
                  </BpkLabel>
                  <BpkSelect
                    className={cls('GuestsRoomsChildren__popover--selectAge')}
                    key={`${item}-${index.toString()}`}
                    id={`childrenAges-${index}`}
                    name="childrenAges"
                    value={item}
                    onChange={(e: any) =>
                      this.childrenAgeHandleChange(e.target.value, index)
                    }
                  >
                    {rangedOptions.map((n: number) => (
                      <option value={n} key={n}>
                        {n < 1
                          ? translate('HotelStay_label_year_old_0')
                          : translatePlural(
                              'HotelStay_label_year_old_',
                              n,
                              '0',
                              null,
                              9,
                              'X',
                            )}
                      </option>
                    ))}
                  </BpkSelect>
                </>
              ))}
            </section>
          </>
        )}

        <div
          className={cls(
            'GuestsRoomsChildren__listItem',
            'GuestsRoomsChildren__listItemRooms',
          )}
        >
          <div className={cls('GuestsRoomsChildren__itemLeft')}>
            <BpkLabel htmlFor="rooms">
              <BpkText tagName="h5" textStyle={TEXT_STYLES.heading5}>
                {translate('SearchControls_label_Rooms')}
              </BpkText>
            </BpkLabel>
            <BpkText
              tagName="p"
              textStyle={TEXT_STYLES.bodyDefault}
              color={TEXT_COLORS.textSecondary}
            >
              {translate('guest_room_panel_room_desc')}
            </BpkText>
          </div>
          <BpkNudger
            id="rooms"
            min={MIN_ROOMS}
            max={MAX_ROOMS}
            value={rooms}
            // @ts-expect-error TS2322: Property 'tabIndex' is not assignable.
            tabIndex={0}
            onValueChange={this.roomsHandleChange}
            decreaseButtonLabel="Decrease"
            increaseButtonLabel="Increase"
          />
        </div>

        {hasFooter && (
          <footer className={cls('GuestsRoomsChildren__footer')}>
            <BpkButtonV2
              disabled={isApplyButtonDisabled}
              onClick={() => {
                onClose?.();
                /**
                 * On DV page, when onsubmit, a new history record will be pushed to browser history stack.
                 * However, when close modal will trigger history.goBack, this method is asynthorous, avoiding
                 * it triggled after new search cycle, so put onSubmit in setTimeout
                 */
                setTimeout(() => {
                  onApply?.(true);
                }, 0);
              }}
              className={cls('GuestsRoomsChildren__applyButton')}
            >
              {translate('apply')}
            </BpkButtonV2>
          </footer>
        )}
      </div>
    );
  }
}

export default withI18n(GuestsRoomsChildren);
